// Thin wrapper around authenticatedFetch for the Amazon Pricing Hub.
// Response shapes are unchanged from the standalone app.
import { authenticatedFetch } from '../AuthComponent';

const BASE = '/api/amazon-pricing-hub';

async function throwIfNotOk(resp) {
  if (resp.ok) return resp;
  let detail = '';
  try {
    const raw = await resp.text();
    try {
      const d = JSON.parse(raw).detail;
      detail = typeof d === 'string' ? d : d?.message ? d.message : raw;
    } catch {
      detail = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 240);
    }
  } catch { /* status alone is enough */ }
  const err = new Error(detail || `${resp.status} ${resp.statusText}`);
  err.status = resp.status;
  throw err;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const getJson = (path, opts) => authenticatedFetch(`${BASE}${path}`, opts).then(throwIfNotOk).then((r) => r.json());
const sendJson = (method, path, body) =>
  authenticatedFetch(`${BASE}${path}`, {
    method,
    headers: JSON_HEADERS,
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then(throwIfNotOk).then((r) => r.json());
const postJson = (path, body) => sendJson('POST', path, body);
const putJson = (path, body) => sendJson('PUT', path, body);
const del = (path) => authenticatedFetch(`${BASE}${path}`, { method: 'DELETE' }).then(throwIfNotOk).then((r) => r.json());

const enc = encodeURIComponent;

export const api = {
  summary: (storeId, fulfillment) =>
    getJson(`/summary?store_id=${enc(storeId)}&fulfillment=${enc(fulfillment)}`),
  // params: search, page, per_page, sort, direction, status, store_id, fulfillment, filters.
  // `opts` is passed to fetch (e.g. { signal }); the raw Response is not exposed.
  products: (params, opts) => {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, v);
    });
    return getJson(`/products?${q.toString()}`, opts);
  },
  brokenCount: (storeId, fulfillment) =>
    getJson(`/products/broken-count?store_id=${enc(storeId)}&fulfillment=${enc(fulfillment)}`),
  inactiveStockCount: (storeId, fulfillment) =>
    getJson(`/products/inactive-stock-count?store_id=${enc(storeId)}&fulfillment=${enc(fulfillment)}`),
  lookup: (pairs) => postJson('/products/lookup', { pairs }),
  updatePrice: (sku, body) => putJson(`/products/${enc(sku)}/price`, body),
  sales: (asin, days, storeId) =>
    getJson(`/products/${enc(asin)}/sales?days=${enc(days)}&store_id=${enc(storeId)}`),
  refreshProduct: (asin, storeId) => postJson(`/products/${enc(asin)}/refresh`, { store_id: storeId }),
  competitors: (asin) => getJson(`/competitors/${enc(asin)}`),
  checkStock: (asin, storeId) => postJson(`/competitors/${enc(asin)}/check-stock`, { store_id: storeId }),
  marks: () => getJson('/marks'),
  mark: (storeId, asin) => postJson(`/marks/${enc(storeId)}/${enc(asin)}`),
  unmark: (storeId, asin) => del(`/marks/${enc(storeId)}/${enc(asin)}`),
  clearMarks: () => del('/marks'),
  preferences: () => getJson('/preferences'),
  savePreferences: (obj) => putJson('/preferences', obj),
  stores: () => getJson('/stores'),
  credentials: () => getJson('/credentials'),
  createStore: (body) => postJson('/stores', body),
  updateStore: (id, body) => putJson(`/stores/${enc(id)}`, body),
  deleteStore: (id) => del(`/stores/${enc(id)}`),
  startSync: (kind, storeId) =>
    postJson(`/sync/${enc(kind)}${storeId !== undefined && storeId !== null ? `?store_id=${enc(storeId)}` : ''}`),
  syncStatus: () => getJson('/sync/status'),
  repricerProgress: () => getJson('/repricer/progress'),
  repricerUpdate: (body) => postJson('/repricer/update', body),
  repricerBulkUpdate: (changes) => postJson('/repricer/bulk-update', { changes }),
};
